/**
 * Calendário de Manutenções (Portal do Condômino, Fase 3, 0.1.34). Mobile-first.
 *
 * O morador vê o calendário; a gestão (síndico/Dono) cadastra, edita e conclui.
 * Reaproveita o alerta de vencimento (vence em breve / vencida) vindo do backend.
 * 0.1.90: manutenção de um bloco/torre só (elevador do Bloco B): avisa e aparece só para quem
 * é daquele bloco.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, CalendarClock, Check, Loader2, Pencil, Plus, Trash2, Wrench } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { SeletorBloco } from "./PublicoSeletor";

interface Manutencao {
  id: number;
  titulo: string;
  tipo: string | null;
  recorrencia: string | null;
  proxima_data: string | null;
  status: string;
  observacao: string | null;
  profissional_id: number | null;
  profissional_nome: string | null;
  documento_id: number | null;
  documento_titulo: string | null;
  dias_para_vencer: number | null;
  alerta: string | null;  // null | "vence" | "vencida"
  bloco_id?: number | null;
  bloco_nome?: string | null;
}
interface ManutencoesResp {
  condominio_id: number;
  itens: Manutencao[];
  pode_gerir: boolean;
}
interface ProfLite { id: number; nome: string }
interface ProfResp { itens: ProfLite[] }

const RECORRENCIAS = ["", "unica", "mensal", "bimestral", "trimestral", "semestral", "anual"];
const STATUS = ["programada", "concluida", "cancelada"];
const STATUS_LABEL: Record<string, string> = {
  programada: "Programada", concluida: "Concluída", cancelada: "Cancelada",
};

const fmtData = (iso: string | null) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

interface FormState {
  id: number | null;
  titulo: string; tipo: string; recorrencia: string;
  proxima_data: string; profissional_id: string; status: string; observacao: string;
  bloco_id: number | null;
}
const VAZIO: FormState = {
  id: null, titulo: "", tipo: "", recorrencia: "", proxima_data: "",
  profissional_id: "", status: "programada", observacao: "", bloco_id: null,
};

export function ManutencoesPage({
  condominioId, onVoltar, gestao = true,
}: {
  condominioId: number;
  onVoltar: () => void;
  gestao?: boolean;   // false = aberto pelo Portal (só consulta, mesmo p/ síndico)
}) {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: ["manutencoes", condominioId] });
  const { data, isLoading } = useQuery<ManutencoesResp>({
    queryKey: ["manutencoes", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/manutencoes`),
  });
  // No Portal a tela é só de consulta; a gestão fica no menu inicial do condomínio.
  const podeGerir = gestao && (data?.pode_gerir ?? false);

  const { data: profs } = useQuery<ProfResp>({
    queryKey: ["profissionais-lite", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/profissionais`),
    enabled: podeGerir,
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: () => {
      const f = form!;
      const corpo = {
        titulo: f.titulo.trim(), tipo: f.tipo.trim() || null,
        recorrencia: f.recorrencia || null, proxima_data: f.proxima_data || null,
        profissional_id: f.profissional_id ? Number(f.profissional_id) : null,
        status: f.status, observacao: f.observacao.trim() || null,
        bloco_id: f.bloco_id,
      };
      return f.id
        ? api.patch(`/manutencoes/${f.id}`, corpo)
        : api.post(`/condominios/${condominioId}/manutencoes`, corpo);
    },
    onSuccess: () => { setForm(null); setErro(null); inval(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar."),
  });
  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/manutencoes/${id}`),
    onSuccess: () => inval(),
  });

  const itens = data?.itens ?? [];

  return (
    <div className="max-w-2xl mx-auto px-1">
      <div className="flex items-center justify-between pt-2 pb-4">
        <button onClick={onVoltar}
          className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="flex items-center gap-2 text-slate-700 font-semibold">
          <Wrench size={18} /> Manutenções
        </div>
      </div>

      {podeGerir && !form && (
        <button onClick={() => { setForm({ ...VAZIO }); setErro(null); }}
          className="mb-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-medium">
          <Plus size={18} /> Nova manutenção
        </button>
      )}

      {form && (
        <div className="mb-4 p-4 rounded-xl border border-slate-200 bg-white space-y-2.5">
          <input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            placeholder="Título (ex.: Limpeza da caixa d'água)"
            className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              placeholder="Tipo (ex.: Elevador)" className="text-sm rounded-md border border-slate-300 px-3 py-2" />
            <select value={form.recorrencia} onChange={(e) => setForm({ ...form, recorrencia: e.target.value })}
              className="text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
              {RECORRENCIAS.map((r) => <option key={r} value={r}>{r ? r[0].toUpperCase() + r.slice(1) : "Recorrência…"}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500 flex flex-col gap-1">Próxima data
              <input type="date" value={form.proxima_data} onChange={(e) => setForm({ ...form, proxima_data: e.target.value })}
                className="text-sm rounded-md border border-slate-300 px-2 py-1.5" />
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">Status
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="text-sm rounded-md border border-slate-300 px-2 py-1.5 bg-white">
                {STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </label>
          </div>
          <select value={form.profissional_id} onChange={(e) => setForm({ ...form, profissional_id: e.target.value })}
            className="w-full text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
            <option value="">Responsável (profissional)…</option>
            {(profs?.itens ?? []).map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          <SeletorBloco condominioId={condominioId} valor={form.bloco_id} onChange={(b) => setForm({ ...form, bloco_id: b })}
            className="w-full text-sm rounded-md border border-slate-300 px-2 py-2 bg-white" />
          <textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })}
            placeholder="Observação (opcional)" rows={2}
            className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" />
          {erro && <div className="text-sm text-rose-700">{erro}</div>}
          <div className="flex gap-2">
            <button onClick={() => salvar.mutate()} disabled={salvar.isPending || !form.titulo.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm disabled:opacity-50">
              {salvar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Salvar
            </button>
            <button onClick={() => { setForm(null); setErro(null); }}
              className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm hover:bg-slate-50">Cancelar</button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-14">
          <Loader2 size={18} className="animate-spin" /> Carregando…
        </div>
      ) : itens.length === 0 ? (
        <div className="text-center text-slate-400 py-14 text-sm">Nenhuma manutenção cadastrada ainda.</div>
      ) : (
        <div className="space-y-2.5">
          {itens.map((m) => (
            <div key={m.id} className="p-4 rounded-xl border border-slate-200 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 flex items-center gap-2">
                    <CalendarClock size={16} className="text-teal-600 flex-shrink-0" /> {m.titulo}
                    {m.bloco_nome && (
                      <span className="text-[11px] font-normal px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-200">{m.bloco_nome}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {m.tipo && <span>{m.tipo} · </span>}
                    Próxima: {fmtData(m.proxima_data)}
                    {m.recorrencia && <span> · {m.recorrencia}</span>}
                    {m.profissional_nome && <span> · {m.profissional_nome}</span>}
                  </div>
                  {m.observacao && <div className="text-sm text-slate-600 mt-1">{m.observacao}</div>}
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                      m.status === "concluida" ? "bg-emerald-50 text-emerald-700"
                        : m.status === "cancelada" ? "bg-slate-100 text-slate-500"
                        : "bg-sky-50 text-sky-700"}`}>{STATUS_LABEL[m.status] ?? m.status}</span>
                    {m.alerta === "vencida" && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 inline-flex items-center gap-1">
                        <AlertTriangle size={11} /> Vencida
                      </span>
                    )}
                    {m.alerta === "vence" && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 inline-flex items-center gap-1">
                        <AlertTriangle size={11} /> Vence em {m.dias_para_vencer} dia(s)
                      </span>
                    )}
                  </div>
                </div>
                {podeGerir && (
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button title="Editar" onClick={() => { setErro(null); setForm({
                      id: m.id, titulo: m.titulo, tipo: m.tipo ?? "", recorrencia: m.recorrencia ?? "",
                      proxima_data: m.proxima_data ?? "", profissional_id: m.profissional_id ? String(m.profissional_id) : "",
                      status: m.status, observacao: m.observacao ?? "", bloco_id: m.bloco_id ?? null,
                    }); }}
                      className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><Pencil size={15} /></button>
                    <button title="Excluir" onClick={() => { if (confirm("Excluir esta manutenção?")) excluir.mutate(m.id); }}
                      className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
